import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../hooks/redux";
import { fetchOrderById } from "../store/slices/ordersSlice";
import {
  Box,
  Typography,
  CircularProgress,
  Paper,
  Divider,
  Chip,
  Button,
  Avatar,
  Tooltip,
  IconButton,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import ArrowBackIosNewRoundedIcon from "@mui/icons-material/ArrowBackIosNewRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import PendingRoundedIcon from "@mui/icons-material/PendingRounded";
import CancelRoundedIcon from "@mui/icons-material/CancelRounded";
import HourglassEmptyRoundedIcon from "@mui/icons-material/HourglassEmptyRounded";
import PauseCircleFilledRoundedIcon from "@mui/icons-material/PauseCircleFilledRounded";
import ErrorRoundedIcon from "@mui/icons-material/ErrorRounded";
import ReplayRoundedIcon from "@mui/icons-material/ReplayRounded";
import BlockRoundedIcon from "@mui/icons-material/BlockRounded";
import ReportProblemRoundedIcon from "@mui/icons-material/ReportProblemRounded";
import HourglassBottomRoundedIcon from "@mui/icons-material/HourglassBottomRounded";
import RedeemRoundedIcon from "@mui/icons-material/RedeemRounded";
import { categoryAPI, orderAPI } from "../services/api";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import StatusChip from "../utils/StatusChip";
import Drawer from "@mui/material/Drawer";
import {
  CloudUpload,
  PictureAsPdf,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
} from "@mui/icons-material";
import PdfPreviewDialog from "../components/common/PdfPreviewer";
import { toast } from "react-toastify";
import ListAltIcon from "@mui/icons-material/ListAlt";

// Date formatter helper
// const formatDate = (raw?: string) => {
//   if (!raw) return "-";
//   // Accept both 'YYYY-MM-DD' and ISO strings
//   const d = new Date(raw.length === 10 ? `${raw}T00:00:00` : raw);
//   if (isNaN(d.getTime())) return raw;
//   return new Intl.DateTimeFormat("en-US", {
//     month: "short",
//     day: "2-digit",
//     year: "numeric",
//   }).format(d); // e.g. Aug 26, 2025
// };

const formatDate = (raw?: string) => {
  if (!raw) return "-";
  // Accept both 'YYYY-MM-DD' and ISO strings
  const d = new Date(raw.length === 10 ? `${raw}T00:00:00` : raw);
  if (isNaN(d.getTime())) return raw;

  return new Intl.DateTimeFormat("en-US", {
    timeZone:"Asia/Kolkata",
    month: "short",
    day: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: true, // for AM/PM, set false for 24h format
  }).format(d); 
};


const getDisplayStatus = (statusCode: number): string => {
  return statusMeta[statusCode]?.label ?? "Unknown Status";
};

const statusMeta: Record<
  number,
  {
    label: string;
    color: "warning" | "success" | "error" | "info";
    icon: React.ReactElement;
    progress: number;
  }
> = {
  1: {
    label: "Pending",
    color: "warning",
    icon: <PendingRoundedIcon fontSize="small" />,
    progress: 5,
  },
  2: {
    label: "In Process",
    color: "info",
    icon: <HourglassEmptyRoundedIcon fontSize="small" />,
    progress: 20,
  },
  5: {
    label: "Completed",
    color: "success",
    icon: <CheckCircleRoundedIcon fontSize="small" />,
    progress: 100,
  },
  7: {
    label: "Cancelled",
    color: "error",
    icon: <CancelRoundedIcon fontSize="small" />,
    progress: 100,
  },
  8: {
    label: "On Hold",
    color: "warning",
    icon: <PauseCircleFilledRoundedIcon fontSize="small" />,
    progress: 30,
  },
  9: {
    label: "Cancelled - Points Reversed",
    color: "error",
    icon: <CancelRoundedIcon fontSize="small" />,
    progress: 100,
  },
  10: {
    label: "Failed",
    color: "error",
    icon: <ErrorRoundedIcon fontSize="small" />,
    progress: 100,
  },
  11: {
    label: "Refunded",
    color: "info",
    icon: <ReplayRoundedIcon fontSize="small" />,
    progress: 100,
  },
  12: {
    label: "Reversed",
    color: "info",
    icon: <ReplayRoundedIcon fontSize="small" />,
    progress: 100,
  },
  13: {
    label: "Chargeback",
    color: "error",
    icon: <ErrorRoundedIcon fontSize="small" />,
    progress: 100,
  },
  14: {
    label: "Expired",
    color: "error",
    icon: <BlockRoundedIcon fontSize="small" />,
    progress: 100,
  },
  15: {
    label: "Processed",
    color: "success",
    icon: <CheckCircleRoundedIcon fontSize="small" />,
    progress: 80,
  },
  16: {
    label: "Voided",
    color: "error",
    icon: <CancelRoundedIcon fontSize="small" />,
    progress: 100,
  },
  17: {
    label: "Out of Stock",
    color: "error",
    icon: <ReportProblemRoundedIcon fontSize="small" />,
    progress: 100,
  },
  18: {
    label: "Client Revert Pending",
    color: "warning",
    icon: <PendingRoundedIcon fontSize="small" />,
    progress: 25,
  },
  19: {
    label: "Partially Complete",
    color: "info",
    icon: <HourglassBottomRoundedIcon fontSize="small" />,
    progress: 60,
  },
  20: {
    label: "Redeemed",
    color: "success",
    icon: <RedeemRoundedIcon fontSize="small" />,
    progress: 100,
  },
  21: {
    label: "Partial Cancel - Points Back",
    color: "warning",
    icon: <ReplayRoundedIcon fontSize="small" />,
    progress: 80,
  },
  22: {
    label: "Partially Refunded",
    color: "info",
    icon: <ReplayRoundedIcon fontSize="small" />,
    progress: 90,
  },
};


const OrderDetails: React.FC = () => {
  const { orderId } = useParams<{ orderId: string }>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { selectedOrder, loading } = useAppSelector((s: any) => s.orders);
  const [drawerOpen, setDrawerOpen] = useState(false);

  // PDF upload states
  const [pdfFiles, setPdfFiles] = useState<File[]>([]);
  const [pdfPreviews, setPdfPreviews] = useState<
    Array<{ name: string; size: number; url: string; displayName: string }>
  >([]);
  const [dragActive, setDragActive] = useState(false);
  const [previewIndex, setPreviewIndex] = useState<number | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [pdfSizeError, setPdfSizeError] = useState(""); // Add this state

  // Add state for order media
  const [ordermedias, setOrderMedia] = useState<
    Array<{
      id: string;
      original_name: string;
      display_name: string;
      created: string;
      download_logs: Array<{ id: string; timestamp: string; ip_address: string }>;
    }>
  >([]);

  // New states for logs modal
  const [logsModalOpen, setLogsModalOpen] = useState(false);
  const [selectedLogs, setSelectedLogs] = useState<any[]>([]);

  // Add state for admin comment
  const [adminComment, setAdminComment] = useState("");
  const [commentLoading, setCommentLoading] = useState(false);
  const [commentError, setCommentError] = useState<string | null>(null);
  const [commentStatus, setCommentStatus] = useState<number>(
    selectedOrder?.orderstatus || 1
  );
  const [isUserNotified, setIsUserNotified] = useState<boolean>(false);

  useEffect(() => {
    if (selectedOrder?.orderstatus) setCommentStatus(selectedOrder.orderstatus);
  }, [selectedOrder]);

  function validateComment(val: string): string | null {
    if (!val || val.trim().length === 0) return "Comment cannot be empty.";
    if (/^[^a-zA-Z0-9]+$/.test(val.trim()))
      return "Comment cannot be only special characters.";
    if (val.trim().length < 3) return "Comment must be at least 3 characters.";
    if (val.trim().length > 200) return "Comment must be at most 200 characters.";
    return null;
  }

  useEffect(() => {
    if (orderId) dispatch(fetchOrderById(orderId));
  }, [dispatch, orderId]);

  useEffect(() => {
    if (selectedOrder?.ordermedias) {
      setOrderMedia(selectedOrder.ordermedias);
    }
  }, [selectedOrder]);

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "70vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }
  if (!selectedOrder) {
    return (
      <Box sx={{ p: 4 }}>
        <Typography variant="h6">Order not found</Typography>
      </Box>
    );
  }

  // Address parse
  // let addrObj: any = {};
  // try {
  //   addrObj = selectedOrder.address ? JSON.parse(selectedOrder.address) : {};
  // } catch {}
  // const billing = addrObj.billingaddress || [];
  // const shipping = addrObj.shippingaddress || [];

  const status = statusMeta[selectedOrder.orderstatus] || statusMeta[1];

  const items = selectedOrder.orderdetail || [];
  const subtotal = items.reduce((s: number, d: any) => s + (d.amount || 0), 0);
  const shippingPrice = selectedOrder.shippingprice ?? 0;
  const discount = selectedOrder.discount ?? 0;
  const grandTotal =
    selectedOrder.totalamount ?? subtotal + shippingPrice - discount;
  const paymentStatusLabel =
    selectedOrder.paymentstatus === 1 ? "Paid" : "Unpaid";

  const progressGradient = `linear-gradient(90deg, ${
    status.color === "success"
      ? "#21c58b"
      : status.color === "error"
      ? "#f44336"
      : "#ffb547"
  } 0%, ${status.progress}%, #e3e7ec ${status.progress}%)`;

  // PDF Handlers
  const handlePdfInput = (files: FileList | null) => {
    if (!files) return;
    const valid = Array.from(files).filter((f) => f.type === "application/pdf");
    if (!valid.length) return;

    // Prevent duplicate file names
    const existingNames = pdfFiles.map((f) => f.name);
    const newUnique = valid.filter((f) => !existingNames.includes(f.name));
    if (newUnique.length < valid.length) {
      setPdfSizeError("Duplicate PDF detected. You cannot upload the same file again.");
      return;
    }

    // Check max file count
    if (pdfFiles.length + newUnique.length > 5) {
      setPdfSizeError("You can upload a maximum of 5 PDF vouchers only ");
      return;
    }

    // Calculate total size including new files
    const currentTotalSize = pdfFiles.reduce((sum, f) => sum + f.size, 0);
    const newFilesTotalSize = newUnique.reduce((sum, f) => sum + f.size, 0);
    const totalSize = currentTotalSize + newFilesTotalSize;

    if (totalSize > 5 * 1024 * 1024) {
      setPdfSizeError("Total voucher size must not exceed 5MB.");
      return;
    } else {
      setPdfSizeError("");
    }

    const newPreviews = newUnique.map((f) => ({
      name: f.name,
      size: f.size,
      url: URL.createObjectURL(f),
      displayName: "", // New field for user input
    }));
    setPdfFiles((prev) => [...prev, ...newUnique]);
    setPdfPreviews((prev) => [...prev, ...newPreviews]);
  };

  const onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handlePdfInput(e.target.files);
    e.target.value = "";
  };

  const removePdf = (idx: number) => {
    setPdfPreviews((prev) => {
      URL.revokeObjectURL(prev[idx].url);
      return prev.filter((_, i) => i !== idx);
    });
    setPdfFiles((prev) => prev.filter((_, i) => i !== idx));
  };

  const clearAll = () => {
    pdfPreviews.forEach((p) => URL.revokeObjectURL(p.url));
    setPdfPreviews([]);
    setPdfFiles([]);
  };

  const handleUploadVoucher = async () => {
    if (!pdfFiles.length) return;
    setIsUploading(true);

    try {
      // 1. Upload all files
      const uploadedMedia: { original_name: string; display_name: string }[] =
        [];
      for (let i = 0; i < pdfFiles.length; i++) {
        const file = pdfFiles[i];
        const formData = new FormData();
        formData.append("fileKey", file, file.name);

        const uploadResponse = await orderAPI.uploadOrderMedia(formData);
        const fileKeyArr = uploadResponse?.data?.result?.files?.fileKey || [];
        fileKeyArr.forEach((f: any) => {
          const preview = pdfPreviews.find(
            (p) => p.name === f.originalFilename
          );
          uploadedMedia.push({
            original_name: f.name,
            display_name: preview?.displayName || "",
          });
        });
      }

      // 2. Call updateOrder API with media array
      await orderAPI.updateOrderVoucher(selectedOrder.id, {
        media: uploadedMedia,
      });

      // 3. Refetch order details
      if (orderId) {
        await dispatch(fetchOrderById(orderId));
      }

      clearAll();
      setDrawerOpen(false);
      toast.success("Vouchers uploaded successfully!");
    } catch (err) {
      toast.error("Failed to upload vouchers.");
    } finally {
      setIsUploading(false);
    }
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") setDragActive(true);
    if (e.type === "dragleave") setDragActive(false);
  };
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files?.length) handlePdfInput(e.dataTransfer.files);
  };

  const handleDisplayNameChange = (idx: number, value: string) => {
    setPdfPreviews((prev) =>
      prev.map((pdf, i) => (i === idx ? { ...pdf, displayName: value } : pdf))
    );
  };

  // Add this handler
  const handleProductClick = (productId: string) => {
    navigate("/products", { state: { viewProductId: productId } });
  };

  // Example: Replace with your actual selector for comments
  const orderComments = selectedOrder?.ordercomments || []; // Array of comments

  return (
    <Box
      sx={{
        p: 3,
        bgcolor: "background.default",
        minHeight: "100vh",
        maxWidth: "1600px",
        mx: "auto",
        display: "flex",
        flexDirection: "column",
        gap: 3,
      }}
    >
      {/* Top Bar */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          display: "flex",
          flexWrap: "wrap",
          gap: 2,
          alignItems: "center",
          justifyContent: "space-between",
          borderRadius: 3,
          border: "1px solid",
          borderColor: "divider",
          background: "linear-gradient(135deg, #ffffff 0%, #f7f9fc 70%)",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Button
            onClick={() => navigate(-1)}
            size="small"
            startIcon={<ArrowBackIosNewRoundedIcon />}
            sx={{ textTransform: "none", fontSize: 14 }}
          >
            Back
          </Button>
          <Box>
            <Typography variant="h4" fontWeight={700} lineHeight={1.15}>
              Order #{selectedOrder.oc_cart_order_id ? selectedOrder.oc_cart_order_id : selectedOrder.orderno}
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mt: 0.25, fontSize: 13 }}
            >
              Placed on {selectedOrder.date} • {items.length} item
              {items.length !== 1 && "s"}
            </Typography>
          </Box>
        </Box>
        <Button
          variant="contained"
          color="warning"
          startIcon={<CloudUploadOutlinedIcon />}
          onClick={() => setDrawerOpen(true)}
          sx={{
            borderRadius: 2,
            fontWeight: 600,
            textTransform: "none",
            px: 3,
            py: 1.1,
            fontSize: 14,
            boxShadow: "0 4px 12px -2px rgba(0,0,0,0.18)",
            "&:hover": {
              boxShadow: "0 6px 16px -2px rgba(0,0,0,0.25)",
            },
          }}
        >
          {ordermedias.length > 0 ? "View & Upload Voucher" : "Upload Voucher"}
        </Button>
      </Paper>

      {/* Unified Info Card (User + Progress + Addresses + Meta) */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          borderRadius: 3,
          border: "1px solid",
          borderColor: "divider",
          background: "#fff",
          display: "grid",
          gridTemplateColumns: { xs: "1fr", lg: "1.1fr 0.9fr" },
          gap: 3,
        }}
      >
        {/* Left column: user + progress + addresses */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {/* User & Progress */}
          <Box
            sx={{
              p: 2.5,
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 2.5,
              background: "linear-gradient(145deg,#ffffff,#fafbfc)",
            }}
          >
            <Box sx={{ display: "flex", gap: 2 }}>
              <Avatar
                sx={{ width: 70, height: 70, fontWeight: 600, fontSize: 24 }}
              >
                {selectedOrder.orderuser?.firstname?.[0]?.toUpperCase() || "U"}
              </Avatar>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  fontWeight={700}
                  fontSize={17}
                  sx={{ display: "flex", alignItems: "center", gap: 1 }}
                >
                  {selectedOrder.orderuser?.firstname}{" "}
                  {selectedOrder.orderuser?.lastname}
                  <Chip
                    size="small"
                    label={paymentStatusLabel}
                    color={paymentStatusLabel === "Paid" ? "success" : "error"}
                    sx={{ height: 22, fontSize: 11 }}
                  />
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  noWrap
                  sx={{ fontSize: 13.5 }}
                >
                  {selectedOrder.orderuser?.email}
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontSize: 12 }}
                >
                  +{selectedOrder.orderuser?.countrycode}{" "}
                  {selectedOrder.orderuser?.cellnumber}
                </Typography>
              </Box>
            </Box>

            {/* Progress bar inline */}
            <Box sx={{ mt: 2 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  mb: 0.5,
                }}
              >
                <Typography variant="caption" color="text.secondary">
                  Status
                </Typography>
                <StatusChip status={selectedOrder.orderstatus} isOrder={true} />
              </Box>

              <Box
                sx={{
                  height: 10,
                  borderRadius: 5,
                  background: progressGradient,
                  position: "relative",
                  overflow: "hidden",
                }}
              >
                <Box
                  sx={{
                    position: "absolute",
                    inset: 0,
                    background:
                      "repeating-linear-gradient(45deg, rgba(255,255,255,.15) 0 6px, transparent 6px 12px)",
                  }}
                />
              </Box>

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  mt: 0.4,
                }}
              >
                <Typography variant="caption" color="text.disabled">
                  Created
                </Typography>
                <Typography variant="caption" color="text.disabled">
                  {getDisplayStatus(selectedOrder.orderstatus)}
                </Typography>
              </Box>

              {/* Show cancellation/rejection reason in red below progress bar */}
              {(selectedOrder.orderstatus === 5 || selectedOrder.orderstatus === 6) && selectedOrder.reason && (
                <Box sx={{ mt: 1.5 }}>
                  <Typography
                    variant="body2"
                    sx={{ color: 'error.main', fontWeight: 700, fontSize: 15 }}
                  >
                    Reason: {selectedOrder.reason}
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>

          {/* Additional Info (replaces separate billing/shipping boxes) */}
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr" }, gap: 2 }}>
            <Box
              sx={{
                p: 2,
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 2,
                background: "#fff",
                display: "flex",
                flexDirection: "column",
                gap: 0.75,
              }}
            >
              <Typography fontWeight={600} fontSize={14}>
                Additional Info
              </Typography>
              <Divider />
              <Box sx={{ display: "flex", justifyContent: "space-between", mt: 1 }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: 12.5 }}>
                  IP Address
                </Typography>
                <Typography variant="caption" sx={{ fontSize: 12.5, color: 'primary.main' }} noWrap>
                  {selectedOrder.ip_address || "-"}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", justifyContent: "space-between", mt: 0.5 }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: 12.5 }}>
                  Language
                </Typography>
                <Typography variant="caption" sx={{ fontSize: 12.5, color: 'primary.main' }} noWrap>
                  {selectedOrder.language || "-"}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Right column: meta & totals */}
        <Box
          sx={{
            p: 2.5,
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 2.5,
            background: "linear-gradient(165deg,#ffffff,#f4f6f9)",
            display: "flex",
            flexDirection: "column",
            gap: 1.1,
          }}
        >
          <Typography fontWeight={700} fontSize={16}>
            Order Meta
          </Typography>
          <MetaPair label="Items" value={selectedOrder.totalitems} />
          <MetaPair label="Quantity" value={selectedOrder.totalquantity} />
          <MetaPair label="Created" value={formatDate(selectedOrder.created)} />
          <MetaPair
            label="Modified"
            value={formatDate(selectedOrder.modified)}
          />
          <MetaPair
            label="Customer"
            value={selectedOrder.orderuser?.username}
          />
          {/* IP Address and Language moved to Additional Info box on the left */}
          <Divider sx={{ my: 1 }} />
          <MetaPair strong label="Subtotal" value={`${subtotal} pts`} />
          {shippingPrice !== 0 && (
            <MetaPair label="Shipping" value={`${shippingPrice} pts`} />
          )}
          {discount !== 0 && (
            <MetaPair label="Discount" value={`- ${discount} pts`} />
          )}

          {/* Show cancellation/rejection reason if applicable */}
          {(selectedOrder.orderstatus === 5 || selectedOrder.orderstatus === 6) && selectedOrder.reason && (
            <MetaPair label="Reason" value={selectedOrder.reason} />
          )}

          <Divider sx={{ my: 1 }} />
          <MetaPair
            strong
            highlight
            label="Total"
            value={`${grandTotal} pts`}
          />
        </Box>
      </Paper>

      {/* Items Table (second row) */}
      <Paper
        elevation={0}
        sx={{
          p: 0,
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 3,
          overflow: "hidden",
          background: "#fff",
        }}
      >
        <Box
          sx={{
            px: 3,
            py: 2.25,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography fontWeight={700} fontSize={18}>
            Items ({items.length})
          </Typography>
          {/* <Typography variant="body2" color="text.secondary" sx={{ fontSize:13 }}>
            Subtotal: {subtotal} pts
          </Typography> */}
        </Box>
        <Divider />
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "minmax(260px,1fr) 110px 150px 150px",
            gap: 0,
            px: 3,
            py: 1,
            fontSize: 13,
            fontWeight: 600,
            color: "text.secondary",
          }}
        >
          <Box>Item Details</Box>
          <Box textAlign="center">Qty</Box>
          <Box textAlign="center">Unit (pts)</Box>
          <Box textAlign="right">Points</Box>
        </Box>
        <Divider />
        <Box>
          {items.map((it: any, idx: number) => {
            const p = it.products || {};
            // const desc = p.description ? p.description.replace(/<[^>]+>/g,'') : '';
            const mediaArr = p.productmedia || [];
            const firstMedia = mediaArr[0];
            const imageUrl = firstMedia?.productname
              ? categoryAPI.getMediaDownloadUrl(firstMedia.productname)
              : undefined;
            return (
              <React.Fragment key={it.id}>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "minmax(260px,1fr) 110px 150px 150px",
                    px: 3,
                    py: 1.9,
                    alignItems: "center",
                    fontSize: 14,
                    "&:hover": { backgroundColor: "action.hover" },
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      gap: 1.9,
                      alignItems: "flex-start",
                      minWidth: 0,
                    }}
                  >
                    <Box
                      sx={{
                        width: 60,
                        height: 60,
                        borderRadius: 1.4,
                        overflow: "hidden",
                        bgcolor: "primary.light",
                        color: "primary.dark",
                        border: "1px solid",
                        borderColor: "divider",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontWeight: 600,
                        fontSize: 20,
                        flexShrink: 0,
                      }}
                    >
                      {imageUrl ? (
                        <Box
                          component="img"
                          src={imageUrl}
                          alt={p.name}
                          sx={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                          }}
                          onError={(e: any) => {
                            (e.target as HTMLImageElement).style.display =
                              "none";
                          }}
                        />
                      ) : (
                        p.name?.[0]?.toUpperCase() || (
                          <ImageOutlinedIcon fontSize="small" />
                        )
                      )}
                    </Box>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography
                        fontSize={15}
                        fontWeight={600}
                        noWrap
                        sx={{ cursor: "pointer", color: "primary.main", textDecoration: "underline" }}
                        onClick={() => handleProductClick(p.id)}
                      >
                        {p.name}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: "block", fontSize: 12 }}
                        noWrap
                      >
                        {p.category?.name || "-"}
                      </Typography>
                    </Box>
                  </Box>
                  <Box textAlign="center">
                    <Typography fontSize={14} fontWeight={600}>
                      {it.quantity}
                    </Typography>
                  </Box>
                  <Box textAlign="center">
                    <Typography fontSize={14}>{p.price}</Typography>
                  </Box>
                  <Box textAlign="right">
                    <Typography fontSize={14} fontWeight={700}>
                      {it.amount}
                    </Typography>
                  </Box>
                </Box>
                {idx < items.length - 1 && <Divider />}
              </React.Fragment>
            );
          })}
        </Box>
        <Divider />
        <Box
          sx={{
            px: 3,
            py: 2.2,
            display: "flex",
            flexDirection: "column",
            gap: 0.55,
            alignItems: "flex-end",
            fontSize: 13,
          }}
        >
          {/* <TotalRow label="Subtotal" value={`${subtotal} pts`} /> */}
          {/* {shippingPrice !== 0 && <TotalRow label="Shipping" value={`${shippingPrice} pts`} />}
          {discount !== 0 && <TotalRow label="Discount" value={`- ${discount} pts`} negative />} */}
          <Divider flexItem sx={{ my: 0.9, width: { xs: "100%", sm: 420 } }} />
          <TotalRow label="Total" value={`${grandTotal} pts`} strong accent />
        </Box>
      </Paper>

      {/* Admin Comment Section */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          borderRadius: 3,
          border: "1px solid",
          borderColor: "divider",
          background: "#fff",
          mb: 3,
        }}
      >
        <Typography variant="h6" fontWeight={700} mb={2}>
          Admin Comment
        </Typography>

        {/* Show previous comments */}
        {orderComments.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" fontWeight={700} mb={1}>
              Previous Comments
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {orderComments.map((c: any) => (
                <Paper
                  key={c.id}
                  elevation={0}
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    border: "1px solid #e2e8f0",
                    background: "#f9fafb",
                    boxShadow: "none",
                    display: "flex",
                    flexDirection: "column",
                    gap: 0.5,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <StatusChip status={c.orderstatus} isOrder={true} />
                    {c.is_user_notified === 1 && (
                      <Chip size="small" label="Notified" color="success" sx={{ ml: 1, fontSize: 11 }} />
                    )}
                    <Typography variant="caption" color="text.secondary" sx={{ ml: "auto" }}>
                      {formatDate(c.created)}
                    </Typography>
                  </Box>
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    {c.comment}
                  </Typography>
                </Paper>
              ))}
            </Box>
          </Box>
        )}

        <Box sx={{ display: "flex", gap: 2, alignItems: "center", mb: 2, flexWrap: 'wrap' }}>
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel id="comment-status-label">Status</InputLabel>
            <Select
              labelId="comment-status-label"
              label="Status"
              value={commentStatus}
              onChange={(e) => setCommentStatus(Number(e.target.value))}
            >
              {Object.entries(statusMeta).map(([key, meta]) => (
                <MenuItem key={key} value={Number(key)}>{meta.label}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControlLabel
            control={<Checkbox checked={isUserNotified} onChange={(e) => setIsUserNotified(e.target.checked)} />}
            label="Notify user"
          />

        </Box>

        {isUserNotified && (
          <Box sx={{ mb: 2 }}>
            <Chip size="small" label="User will be notified" color="info" />
          </Box>
        )}

        <TextField
          label="Comment"
          multiline
          minRows={2}
          maxRows={6}
          fullWidth
          value={adminComment}
          onChange={(e) => {
            setAdminComment(e.target.value);
            setCommentError(validateComment(e.target.value));
          }}
          error={!!commentError}
          helperText={commentError || ""}
          sx={{ mb: 2 }}
        />
        <Box sx={{ display: "flex", alignItems: "flex-end", gap: 2 }}>
          <Button
            variant="contained"
            color="primary"
            disabled={commentLoading || !!commentError || !adminComment}
            onClick={async () => {
              const err = validateComment(adminComment);
              if (err) {
                setCommentError(err);
                return;
              }
              setCommentLoading(true);
              try {
                await orderAPI.uploadOrderComment(orderId!, {
                  comment: adminComment,
                  orderstatus: commentStatus,
                  is_user_notified: isUserNotified ? 1 : 0,
                });
                toast.success("Comment added successfully!");
                setAdminComment("");
                setIsUserNotified(false);
                if (orderId) {
                  await dispatch(fetchOrderById(orderId)); // refresh order details
                }
              } catch (err) {
                toast.error("Failed to add comment.");
              } finally {
                setCommentLoading(false);
              }
            }}
            sx={{ mb: 2, minWidth: 140 }}
          >
            {commentLoading ? "Saving..." : "Add Comment"}
          </Button>
        </Box>
      </Paper>

      {/* Drawer for PDF Upload */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{
          sx: {
            width: 600,
            bgcolor: "#fff",
            borderRadius: 0,
          },
        }}
      >
        <Box
          sx={{
            flex: 1,
            p: 3,
            display: "flex",
            flexDirection: "column",
            gap: 3,
            bgcolor: "#fff",
            minHeight: "100vh",
            overflowY: "auto",
            position: "relative",
          }}
        >
          {/* Title at top */}
          <Typography
            variant="h5"
            fontWeight={800}
            sx={{ mb: 1, color: "black" }}
          >
            {ordermedias.length > 0
              ? "View & Upload Voucher"
              : "Upload Voucher"}
          </Typography>

          {/* Loader overlay when uploading - covers entire Drawer */}
          {isUploading && (
            <Box
              sx={{
                position: "fixed",
                top: 0,
                left: 0,
                width: "100vw",
                height: "100vh",
                bgcolor: "rgba(255,255,255,0.75)",
                zIndex: 9999,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                pointerEvents: "all",
              }}
            >
              <CircularProgress color="warning" size={48} />
              <Typography sx={{ mt: 2, fontWeight: 700, color: "#333" }}>
                Uploading vouchers...
              </Typography>
            </Box>
          )}

          <Paper
            elevation={0}
            onDragEnter={handleDrag}
            onDragOver={handleDrag}
            onDragLeave={handleDrag}
            onDrop={handleDrop}
            sx={{
              p: 2.5,
              border: "1px dashed",
              borderColor: dragActive ? "primary.main" : "#cbd5e1",
              borderRadius: 2,
              bgcolor: dragActive ? "primary.lighter" : "#f8fafc",
              textAlign: "center",
              transition: "all .2s",
            }}
          >
            <input
              id="voucher-pdf-input"
              type="file"
              accept="application/pdf"
              multiple
              hidden
              onChange={onFileChange}
            />
            <CloudUpload sx={{ fontSize: 44, color: "primary.main", mb: 1 }} />
            <Typography variant="body2" sx={{ mb: 1 }}>
              Drag & drop or click to select PDF files
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontWeight: 500 }}>
              You can upload max 5 PDFs with total size of 5MB.
            </Typography>
            <Button
              variant="contained"
              size="small"
              onClick={() =>
                document.getElementById("voucher-pdf-input")?.click()
              }
              sx={{ textTransform: "none", fontWeight: 600 }}
            >
              Select PDFs
            </Button>
          </Paper>

          {/* Show PDF size/count error below upload area */}
          {pdfSizeError && (
            <Typography color="error" variant="body2" sx={{ mb: 2, fontWeight: 500 }}>
              {pdfSizeError}
            </Typography>
          )}

          {pdfPreviews.length > 0 && (
            <Paper
              elevation={0}
              sx={{ p: 2, border: "1px solid #e2e8f0", borderRadius: 2 }}
            >
              <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                <Typography
                  variant="subtitle2"
                  fontWeight={700}
                  sx={{ flex: 1 }}
                >
                  Selected Files ({pdfPreviews.length})
                </Typography>
                <Tooltip title="Clear all">
                  <Button
                    size="small"
                    color="error"
                    onClick={clearAll}
                    sx={{ textTransform: "none" }}
                  >
                    Clear
                  </Button>
                </Tooltip>
              </Box>
              <Divider sx={{ mb: 1.5 }} />
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                {pdfPreviews.map((pdf, idx) => (
                  <Paper
                    key={pdf.url}
                    variant="outlined"
                    sx={{
                      p: 2,
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      borderRadius: 2,
                      background: "#f9fafb",
                      boxShadow: "none",
                      border: "1px solid #e2e8f0",
                    }}
                  >
                    <PictureAsPdf color="error" sx={{ fontSize: 34, mr: 2 }} />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        variant="body2"
                        fontWeight={700}
                        sx={{
                          wordBreak: "break-all",
                          lineHeight: 1.2,
                          mb: 0.5,
                        }}
                      >
                        {pdf.name}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ mb: 1, display: "block" }}
                      >
                        {(pdf.size / 1024).toFixed(1)} KB
                      </Typography>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <TextField
                          size="small"
                          label="Display Name (shown to agents)"
                          value={pdf.displayName}
                          onChange={(e) => handleDisplayNameChange(idx, e.target.value)}
                          placeholder="e.g. Invoice, Voucher, etc."
                          fullWidth
                          sx={{
                            "& .MuiInputBase-root": { bgcolor: "#fff" },
                            "& .MuiInputLabel-root": {
                              fontWeight: 500,
                              fontSize: 13,
                            },
                          }}
                          error={!!pdf.displayName && pdf.displayName.trim() === ""}
                          helperText={!!pdf.displayName && pdf.displayName.trim() === "" ? "Name cannot be only spaces." : ""}
                        />
                        <Tooltip title="Preview">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => setPreviewIndex(idx)}
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Remove">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => removePdf(idx)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>
                  </Paper>
                ))}
              </Box>
            </Paper>
          )}

          {/* Show existing order media files */}
          {ordermedias.length > 0 && (
            <Paper
              elevation={0}
              sx={{
                p: 2,
                border: "1px solid #e2e8f0",
                borderRadius: 2,
                mb: 2,
              }}
            >
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                Existing Vouchers
              </Typography>
              <Divider sx={{ mb: 1.5 }} />
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                {ordermedias.map((media) => (
                  <Paper
                    key={media.id}
                    variant="outlined"
                    sx={{
                      p: 2,
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      borderRadius: 2,
                      background: "#f9fafb",
                      boxShadow: "none",
                      border: "1px solid #e2e8f0",
                    }}
                  >
                    <PictureAsPdf color="error" sx={{ fontSize: 34, mr: 2 }} />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        variant="body2"
                        fontWeight={700}
                        sx={{
                          wordBreak: "break-all",
                          lineHeight: 1.2,
                          mb: 0.5,
                        }}
                      >
                        {media.original_name}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ mb: 1, display: "block" }}
                      >
                        {media.display_name || (
                          <span style={{ color: "#aaa" }}>No display name</span>
                        )}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.disabled"
                        sx={{ fontSize: 11 }}
                      >
                        {formatDate(media.created)}
                      </Typography>
                    </Box>
                    <Tooltip title="View PDF">
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => {
                          const token = localStorage.getItem("accessToken") || "";
                          const url = orderAPI.getOrderMediaDownloadUrlwithToken(
                            media.original_name,
                            token
                          );
                          window.open(url, "_blank");
                        }}
                      >
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={async () => {
                          try {
                            await orderAPI.deleteOrderMedia(media.id);
                            toast.success("Voucher deleted!");
                            if (orderId) {
                              await dispatch(fetchOrderById(orderId));
                            }
                            setDrawerOpen(true); // Keep drawer open
                          } catch {
                            toast.error("Failed to delete voucher.");
                          }
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {media.download_logs && media.download_logs.length > 0 && (
                      <Tooltip title="View Download Logs">
                        <IconButton
                          size="small"
                          color="info"
                          onClick={() => {
                            setSelectedLogs(media.download_logs);
                            setLogsModalOpen(true);
                          }}
                        >
                          <ListAltIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Paper>
                ))}
              </Box>
            </Paper>
          )}

          {/* Buttons at the end */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 2,
              mt: "auto",
            }}
          >
            <Button
              variant="outlined"
              onClick={() => setDrawerOpen(false)}
              disabled={isUploading}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                color: "#555",
                borderColor: "#f3e8ff",
                background: "#fff",
                boxShadow: "0 2px 8px rgba(0,0,0,0.07)",
                "&:hover": {
                  background: "#f5f5f5",
                  borderColor: "#f3e8ff",
                },
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleUploadVoucher}
              disabled={!pdfFiles.length || isUploading}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                bgcolor: "#f8991d",
                color: "#fff",
                boxShadow: "0 2px 8px rgba(0,0,0,0.07)",
                "&:hover": {
                  bgcolor: "#e08813",
                },
              }}
            >
              Save
            </Button>
          </Box>
        </Box>

        {/* PDF Preview Dialog */}
        <PdfPreviewDialog
          open={previewIndex !== null}
          file={
            previewIndex !== null
              ? {
                  name: pdfPreviews[previewIndex].name,
                  url: pdfPreviews[previewIndex].url,
                }
              : undefined
          }
          onClose={() => setPreviewIndex(null)}
          onRemove={
            previewIndex !== null
              ? () => {
                  removePdf(previewIndex);
                  setPreviewIndex(null);
                }
              : undefined
          }
        />

        {/* Logs Modal */}
        {logsModalOpen && (
          <Dialog
            open={logsModalOpen}
            onClose={() => setLogsModalOpen(false)}
            maxWidth="md"
            fullWidth
            PaperProps={{
              sx: {
                borderRadius: 3,
                p: 0,
                minHeight: 400,
              },
            }}
          >
            <DialogTitle>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <ListAltIcon color="primary" />
                <Typography variant="h6" fontWeight={700}>
                  Voucher Download Logs
                </Typography>
              </Box>
            </DialogTitle>
            <DialogContent sx={{ p: 3 }}>
              {selectedLogs.length === 0 ? (
                <Typography>No logs found.</Typography>
              ) : (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  {selectedLogs.map((log) => (
                    <Paper
                      key={log.id}
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        background: "#f9fafb",
                        boxShadow: "none",
                        border: "1px solid #e2e8f0",
                        display: "flex",
                        flexDirection: "column",
                        gap: 1,
                      }}
                    >
                      <Typography fontWeight={700}>
                        User: {log.userId && selectedOrder.user && log.userId === selectedOrder.user.id
                          ? `${selectedOrder.user.firstname} ${selectedOrder.user.lastname}`
                          : log.userId}
                      </Typography>
                      <Typography>
                        Device: {log.device || "-"}
                      </Typography>
                      <Typography>
                        Time: {log.download_time
                          ? new Date(log.download_time).toLocaleString()
                          : "-"}
                      </Typography>
                      <Typography fontSize={12} color="text.secondary">
                        IP: {log.ip_address || "-"}
                      </Typography>
                    </Paper>
                  ))}
                </Box>
              )}
            </DialogContent>
            <Box sx={{ p: 2, display: "flex", justifyContent: "flex-end" }}>
              <Button variant="contained" onClick={() => setLogsModalOpen(false)}>
                Close
              </Button>
            </Box>
          </Dialog>
        )}
      </Drawer>
    </Box>
  );
};

const MetaPair = ({
  label,
  value,
  strong,
  highlight,
}: {
  label: string;
  value: any;
  strong?: boolean;
  highlight?: boolean;
}) => (
  <Box sx={{ display: "flex", justifyContent: "space-between", fontSize: 13 }}>
    <Typography
      variant="caption"
      color="text.secondary"
      sx={{ fontSize: 12.5 }}
    >
      {label}
    </Typography>
    <Typography
      variant="caption"
      fontWeight={strong ? 700 : 600}
      sx={{
        fontSize: 12.5,
        color: highlight ? "primary.main" : "text.primary",
      }}
      noWrap
    >
      {value || "-"}
    </Typography>
  </Box>
);

const TotalRow = ({
  label,
  value,
  strong,
  accent,
  negative,
}: {
  label: string;
  value: string;
  strong?: boolean;
  accent?: boolean;
  negative?: boolean;
}) => (
  <Box
    sx={{
      display: "flex",
      justifyContent: "space-between",
      width: { xs: "100%", sm: 420 },
    }}
  >
    <Typography variant="body2" color="text.secondary" sx={{ fontSize: 13 }}>
      {label}
    </Typography>
    <Typography
      variant="body2"
      sx={{
        fontSize: 13,
        fontWeight: strong ? 700 : 600,
        color: accent
          ? "primary.main"
          : negative
          ? "error.main"
          : "text.primary",
      }}
    >
      {value}
    </Typography>
  </Box>
);

// const MiniAddress = ({ title, list }: { title: string; list: any[] }) => (
//   <Box
//     sx={{
//       p: 2,
//       border: "1px solid",
//       borderColor: "divider",
//       borderRadius: 2,
//       background: "#fff",
//       display: "flex",
//       flexDirection: "column",
//       gap: 0.75,
//     }}
//   >
//     <Typography fontWeight={600} fontSize={14}>
//       {title}
//     </Typography>
//     <Divider />
//     {list.length === 0 && (
//       <Typography
//         variant="caption"
//         color="text.secondary"
//         sx={{ fontSize: 12 }}
//       >
//         No {title.toLowerCase()} added
//       </Typography>
//     )}
//     {list.map((a) => (
//       <Box key={a.id}>
//         <Typography variant="body2" fontWeight={600} sx={{ fontSize: 13 }}>
//           {a.name}
//         </Typography>
//         <Typography
//           variant="caption"
//           color="text.secondary"
//           sx={{ display: "block", fontSize: 11.5 }}
//         >
//           {a.addressline1}
//           {a.addressline2 && `, ${a.addressline2}`}, {a.city}, {a.state},{" "}
//           {a.zipcode}, {a.country}
//         </Typography>
//         {a.phone && (
//           <Typography
//             variant="caption"
//             color="text.disabled"
//             display="block"
//             sx={{ fontSize: 11 }}
//           >
//             +{a.countrycode} {a.phone} {a.isdefault && "• Default"}
//           </Typography>
//         )}
//       </Box>
//     ))}
//   </Box>
// );

export default OrderDetails;
